import React, { useEffect, useContext, useState, useCallback } from "react";
import { useDispatch, useSelector, connect } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  fetchOrgCodes,
  clearOrgCodesState,
} from "../../store/auth/getOrgScope/orgScopeSlice";
import {
  CircularProgress,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Slide,
  Typography,
  Menu,
  MenuItem,
  AppBar,
  Toolbar,
  Button,
  Chip,
  Tooltip,
  TextField,
  InputAdornment,
  Box,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import LoginIcon from "@mui/icons-material/Login";
import AccountCircle from "@mui/icons-material/AccountCircle";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import { OrgContext } from "../../OrgContext";
import logolight from "../../assets/images/Expanded_WhiteOrange_Curve.png";
import AddOrganizationModal from "./AddOrganizationModal";
import { RESET_STORE } from "../../store/actionTypes";

const container = {
  display: "flex",
  flexDirection: "row",
  justifyContent: "center",
  alignItems: "center",
  height: "100vh",
  width: "100%",
  backgroundColor: "#F4F6F8",
};

const outerBox = {
  backgroundColor: "white",
  boxShadow: "0px 10px 20px rgba(0, 0, 0, 0.2)",
  borderRadius: "0 0 0.5rem 0.5rem",
  width: "700px",
  height: "80vh",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

const contentArea = {
  flexGrow: 1,
  padding: "1rem",
  overflowY: "auto",
};

const tableContainer = {
  marginTop: "1rem",
  borderRadius: "0 0 0.5rem 0.5rem",
  boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
};

const tableHeader = {
  textAlign: "center",
  backgroundColor: "#E0E0E0",
};

const OrgSelection = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { isLoading, orgCodes, errorMessage, msp, mspOrgCode } = useSelector(
    (state) => state.orgScope
  );

  const { setHasSelectedOrganization } = useContext(OrgContext);

  const [anchorEl, setAnchorEl] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("All");

  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleSuccess = useCallback(() => {
    dispatch(fetchOrgCodes()); // Refresh organization list
    handleCloseModal(); // Close the modal
  }, [dispatch]);

  useEffect(() => {
    // Reset wafrOnly in localStorage when component mounts
    localStorage.setItem("wafrOnly", "false");

    setHasSelectedOrganization(false);
    dispatch(fetchOrgCodes());
    
    return () => {
      dispatch(clearOrgCodesState());
    };
  }, [dispatch]);


  // Set mspOrgCode in localStorage after API call completes and mspOrgCode is available
  useEffect(() => {
    if (!isLoading && mspOrgCode) {
      console.log("🚀 ~ Setting mspOrgCode in localStorage:", mspOrgCode);
      localStorage.setItem("mspOrgCode", mspOrgCode);
    }
  }, [isLoading, mspOrgCode]);

  // Save whole scope data to localStorage when orgCodes are fetched
  useEffect(() => {
    if (!isLoading && orgCodes && orgCodes.length > 0) {
      console.log("🚀 ~ Saving scope data to localStorage:", orgCodes);
      localStorage.setItem("orgScopeData", JSON.stringify(orgCodes));
    }
  }, [isLoading, orgCodes]);

  useEffect(() => {
    if (!isLoading && !msp) {
      // Check if there's a pending orgcode to auto-select first
      const savedOrgCode = localStorage.getItem("pendingOrgCode");
      if (savedOrgCode) {
        // Don't auto-select single org if there's a pending orgcode
        return;
      }
      if (orgCodes.length === 1) {
        handleOrganizationClick(orgCodes[0]);
      }
      localStorage.setItem("isMspOrg", false);
    }
  }, [isLoading, orgCodes, msp]);

  const handleSnackbarClose = () => {
    dispatch(clearOrgCodesState());
  };

  // Margin Managment UI -> Only Visible in MSP organization
  const handleMarginMangementUIVisibility = (org) => {
    console.log("🚀 ~ handleMarginMangementUIVisibility ~ org:", org)
    const value = (org.orgCode === mspOrgCode && msp) ? "true" : "false";
    localStorage.setItem("isMarginMangementUIVisible", value);
  }

  // Margin management Dashboard -> Cost Spend (W/O Margin) -> Visible only to MSP user in NON - MSP Organization
  const handleMarginMangementDashboardVisibility = (org) => {
    console.log("🚀 ~ handleMarginMangementDashboardVisibility ~ org:", org)
    const value = (org.orgCode !== mspOrgCode && msp) ? "true" : "false";
    localStorage.setItem("isMarginmanagementDashboardVisible", value);
  }

  // Customer Summary Dashboard -> Visible only in MSP organization
  const isCustomerSummaryVisible = (org) => {
    console.log("🚀 ~ isCustomerSummaryVisible ~ org:", org)
    const value = (org.orgCode === mspOrgCode && msp) ? "true" : "false";
    localStorage.setItem("isCustomerSummaryDashboardVisible", value);
  }

  const handleOrganizationClick = (org) => {
    dispatch({ type: RESET_STORE });
    console.log("🚀 ~ handleOrganizationClick ~ org:", org);
    
    // Clear the cameFromMarginManagement flag when selecting any organization
    // This ensures the back button disappears when user manually selects an org
    localStorage.removeItem("cameFromMarginManagement");
    
    localStorage.setItem("selectedOrganization", JSON.stringify(org));
    localStorage.setItem("hasSelectedOrganization", true);
    if(msp) {
      localStorage.setItem("isMspOrg", true);
    }
    // Set wafrOnly based on the selected organization
    localStorage.setItem("wafrOnly", org.wafrOnly);

    // Margin Management - UI
    handleMarginMangementUIVisibility(org);

    // Margin Management Kibana dashboard
    handleMarginMangementDashboardVisibility(org);

    // Customer Summary Dashboard visibility
    isCustomerSummaryVisible(org);

    setHasSelectedOrganization(true);
    console.log("🚀 ~ handleOrganizationClick ~ wafrOnly:", org.wafrOnly);
    
    // Check if we're coming from margin management (should redirect to spend-categorization)
    const redirectFromMarginManagement = localStorage.getItem("redirectFromMarginManagement");
    if (redirectFromMarginManagement === "true") {
      // Clear the flag
      localStorage.removeItem("redirectFromMarginManagement");
      history.push("/costinsight");
      return;
    }
    
    if (org.wafrOnly) {
      history.push("/workload-assessment/");
      return;
    }
    history.push("/dashboard");
  };

  // Auto-select organization from saved orgcode when orgCodes are loaded
  useEffect(() => {
    if (!isLoading && orgCodes && orgCodes.length > 0) {
      const savedOrgCode = localStorage.getItem("pendingOrgCode");
      if (savedOrgCode) {
        const matchingOrg = orgCodes.find(org => org.orgCode === savedOrgCode);
        if (matchingOrg) {
          console.log("🚀 ~ Auto-selecting org from saved orgcode:", matchingOrg);
          // Clear the pending orgcode immediately
          localStorage.removeItem("pendingOrgCode");
          
          // Dispatch RESET_STORE explicitly before auto-selection to ensure clean state
          dispatch({ type: RESET_STORE });
          
          // Add a small delay to ensure store reset completes before auto-selecting
          setTimeout(() => {
            handleOrganizationClick(matchingOrg);
          }, 300);
        } else {
          // Clear if no match found
          console.log("🚀 ~ No matching org found for saved orgcode:", savedOrgCode);
          localStorage.removeItem("pendingOrgCode");
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, orgCodes]);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    history.push("/logout");
  };

  // Filter and sort orgCodes
  const filteredAndSortedOrgCodes = Array.isArray(orgCodes)
    ? [...orgCodes]
        .filter((org) => {
          // Apply filter
          if (filter === "Customer") {
            if (!org.customer) return false;
          } else if (filter === "Sub-org") {
            if (org.orgCode === mspOrgCode) return false; // Exclude primary/MSP org
            if (org.customer) return false; // Exclude customer organizations
          }
          // Filter "All" doesn't need additional filtering

          // Apply search
          if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            return (
              org.orgName.toLowerCase().includes(searchLower) ||
              org.orgCode.toLowerCase().includes(searchLower)
            );
          }
          return true;
        })
        .sort((a, b) => {
          if (a.orgCode === mspOrgCode) return -1; // MSP stays first
          if (b.orgCode === mspOrgCode) return 1;
          return a.orgName.localeCompare(b.orgName); // Sort by orgName
        })
    : [];

  return (
    <div style={container}>
      <Slide direction="down" in={true} mountOnEnter unmountOnExit>
        <div style={outerBox}>
          {/* Title Bar with Logo and Logout */}
          <AppBar position="static" style={{ backgroundColor: "#193A6F" }}>
            <Toolbar style={{ justifyContent: "space-between" }}>
              <img
                src={logolight}
                alt="Logo"
                style={{ height: "40px", cursor: "pointer" }}
                onClick={() => history.push("/")}
              />
              <div>
                <IconButton
                  size="large"
                  edge="end"
                  color="inherit"
                  onClick={handleMenu}
                >
                  <AccountCircle />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleClose}
                >
                  <MenuItem onClick={handleLogout}>Logout</MenuItem>
                </Menu>
              </div>
            </Toolbar>
          </AppBar>

          {/* Content Area */}
          <div style={contentArea}>
            {/* Search and Filter Section */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "1rem",
                gap: "1rem",
                flexWrap: "wrap",
              }}
            >
              {/* Search Field */}
              <TextField
                placeholder="Search organizations..."
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  flexGrow: 1,
                  minWidth: "200px",
                }}
              />

              {/* Filter Buttons */}
              <ToggleButtonGroup
                value={filter}
                exclusive
                onChange={(e, newFilter) => {
                  if (newFilter !== null) setFilter(newFilter);
                }}
                size="small"
                sx={{
                  "& .MuiToggleButton-root": {
                    border: "1px solid #E0E0E0",
                    color: "#757575",
                    "&.Mui-selected": {
                      backgroundColor: "#F98125",
                      color: "white",
                      "&:hover": {
                        backgroundColor: "#E07015",
                      },
                    },
                    "&:hover": {
                      backgroundColor: "#F5F5F5",
                    },
                  },
                }}
              >
                <ToggleButton value="All">All</ToggleButton>
                <ToggleButton value="Sub-org">Sub-org</ToggleButton>
                <ToggleButton value="Customer">Customer</ToggleButton>
              </ToggleButtonGroup>

              {/* Add Organization Button */}
              {isLoading ? (
                <CircularProgress size={24} style={{ color: "#F98125" }} />
              ) : (
                msp && (
                  <Button
                    variant="contained"
                    style={{ backgroundColor: "#F98125" }}
                    startIcon={<AddIcon />}
                    onClick={handleOpenModal}
                  >
                    Invite Sub Organization
                  </Button>
                )
              )}
            </Box>

            {isLoading ? (
              <div style={{ textAlign: "center", margin: "20px" }}>
                <CircularProgress />
              </div>
            ) : (
              <TableContainer component={Paper} style={tableContainer}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell colSpan={2} style={tableHeader}>
                        <Typography variant="h6" fontWeight="bold">
                          Organization List
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredAndSortedOrgCodes.length > 0 ? (
                      filteredAndSortedOrgCodes.map((org) => (
                        <TableRow
                          key={org.orgCode}
                          hover
                          style={{
                            transition: "background-color 0.3s",
                            cursor: "pointer",
                            borderBottom: "1px solid #E0E0E0",
                          }}
                        >
                          <Tooltip
                            title={
                              org.wafrOnly
                                ? "Well Architected Framework"
                                : org.customer
                                ? "Financial Operations"
                                : "Cloudcatcher Full Feature Access"
                            }
                            arrow
                          >
                            <TableCell
                              align="center"
                              style={{ padding: "16px" }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                }}
                              >
                                <Typography variant="body1" fontWeight="bold">
                                  {org.orgName}
                                </Typography>
                                {org.orgCode === mspOrgCode && (
                                  <span
                                    style={{
                                      display: "inline-block",
                                      fontSize: "12px",
                                      color: "#1976D2",
                                      backgroundColor: "#E3F2FD",
                                      padding: "2px 8px",
                                      borderRadius: "12px",
                                      fontWeight: "500",
                                    }}
                                  >
                                    Primary
                                  </span>
                                )}
                                <span
                                  style={{
                                    display: "inline-block",
                                    fontSize: "12px",
                                    color: org.wafrOnly
                                      ? "#FF9800"
                                      : org.customer
                                      ? "#9C27B0"
                                      : "#4CAF50",
                                    backgroundColor: org.wafrOnly
                                      ? "#FFF3E0"
                                      : org.customer
                                      ? "#F3E5F5"
                                      : "#E8F5E9",
                                    padding: "2px 8px",
                                    borderRadius: "12px",
                                    fontWeight: "500",
                                    cursor: "pointer",
                                  }}
                                >
                                  {org.wafrOnly
                                    ? "WAFR"
                                    : org.customer
                                    ? "FinOps"
                                    : "All Features"}
                                </span>
                              </div>
                            </TableCell>
                          </Tooltip>
                          <TableCell align="center" style={{ padding: "16px" }}>
                            <IconButton
                              color="primary"
                              onClick={() => handleOrganizationClick(org)}
                              style={{
                                borderRadius: "50%",
                                border: "1px solid #1976D2",
                                padding: "6px",
                              }}
                            >
                              <LoginIcon style={{ fontSize: "20px" }} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={2}
                          style={{ textAlign: "center", padding: "20px" }}
                        >
                          <Typography
                            variant="body1"
                            style={{ color: "#757575" }}
                          >
                            No organizations available.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Snackbar
              open={Boolean(errorMessage)}
              autoHideDuration={6000}
              onClose={handleSnackbarClose}
              message={errorMessage}
              anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            />
          </div>
        </div>
      </Slide>

      {/* Add Organization Modal */}
      <AddOrganizationModal
        open={isModalOpen}
        onClose={handleCloseModal}
        onSuccess={handleSuccess}
        mspOrgCode={mspOrgCode}
      />
    </div>
  );
};

export default OrgSelection;
